import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import './Queue.css';

/**
 * Global accept-match modal — rendered at App.js level so it shows on every
 * route, not only /queue. Mirrors the PlayAgainBanner pattern.
 *
 * Props are owned by App.js: the parent feeds players + statuses (updated
 * via WebSocket accept_update events) and provides accept/decline handlers.
 * The countdown is purely UI — backend enforces the real timeout.
 *
 * allowCancelWhenAccepted: when true, a player who has already accepted still
 * sees a cancel button (which calls onDecline). The /create-match modal sets
 * this so the creator — auto-accepted server-side — isn't trapped on "waiting
 * for others" with no way out, especially when the accept timeout is "no
 * limit" (0). The queue modal leaves it false: in matchmaking, an accept is
 * final and mustn't be rescindable, or a single player could grief the cohort.
 */
export default function AcceptMatchBanner({
  players,
  statuses,
  acceptTimeout,
  onAccept,
  onDecline,
  allowCancelWhenAccepted = false,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [countdown, setCountdown] = useState(
    acceptTimeout > 0 ? acceptTimeout : null,
  );
  // Local guard against double-click — disables the buttons immediately
  // when the user clicks, even before the WebSocket-driven status update
  // arrives. Without this, a second click can fire a second POST and
  // contribute to the AcceptMatchView race that produced the 65/66
  // duplicate-match incident on 2026-05-07.
  const [submitting, setSubmitting] = useState(false);
  const intervalRef = useRef(null);

  // Reset both countdown and submitting whenever a new modal appears.
  useEffect(() => {
    setCountdown(acceptTimeout > 0 ? acceptTimeout : null);
    setSubmitting(false);
  }, [acceptTimeout]);

  useEffect(() => {
    if (countdown === null || countdown <= 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          if (onDecline) onDecline();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown !== null && countdown > 0]);

  const userAccepted =
    user?.discord_id && statuses?.[user.discord_id] === 'accepted';

  return (
    <div className="accept-modal-overlay">
      <div className="accept-modal">
        <h2>{t('queue.matchFound')}</h2>
        {countdown !== null && countdown > 0 && (
          <div className="accept-countdown">{countdown}s</div>
        )}
        <div className="accept-player-list">
          {players?.map((p) => {
            const status = statuses?.[p.discord_id] || 'pending';
            const icon =
              status === 'accepted' ? '✅' : status === 'declined' ? '❌' : '⏳';
            return (
              <div key={p.discord_id} className={`accept-player ${status}`}>
                <span className="accept-player-icon">{icon}</span>
                <span className="accept-player-name">{p.username}</span>
              </div>
            );
          })}
        </div>
        {userAccepted ? (
          <>
            <p className="accept-waiting">{t('queue.waitingOthers')}</p>
            {allowCancelWhenAccepted && (
              <div className="accept-buttons">
                <button
                  className="decline-btn"
                  onClick={async () => {
                    if (submitting) return;
                    setSubmitting(true);
                    try {
                      if (onDecline) await onDecline();
                    } catch (e) {
                      setSubmitting(false);
                    }
                  }}
                  disabled={submitting}
                >
                  {submitting ? t('queue.sending') : t('queue.cancelMatch')}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="accept-buttons">
            <button
              className="accept-btn"
              onClick={async () => {
                if (submitting) return;
                setSubmitting(true);
                try {
                  if (onAccept) await onAccept();
                } catch (e) {
                  // Reset on failure so user can retry instead of being
                  // stuck on "Skickar…" until countdown.
                  setSubmitting(false);
                }
              }}
              disabled={submitting}
            >
              {submitting ? t('queue.sending') : t('queue.accept')}
            </button>
            <button
              className="decline-btn"
              onClick={async () => {
                if (submitting) return;
                setSubmitting(true);
                try {
                  if (onDecline) await onDecline();
                } catch (e) {
                  setSubmitting(false);
                }
              }}
              disabled={submitting}
            >
              {t('queue.decline')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
